<template>
  <div class="edit-file-container">
    <!-- 顶部菜单 -->
    <div class="top_menu">
      <div class="title" style="background: #F5F6F7">
        <img src="@/assets/logo.png" class="logo" alt="logo" />
        <span>瑞银文件管理系统 - 在线编辑</span>
      </div>
      <div class="file-info">
        <span class="file-name">{{ fileInfo ? fileInfo.fileName : '加载中...' }}</span>
      </div>
      <ul>
        <li>
          <user-pull-down>
            <span>{{ userName }}<i class="el-icon-arrow-down el-icon--right"></i></span>
          </user-pull-down>
        </li>
      </ul>
    </div>

    <!-- 编辑器区域 -->
    <div class="editor-wrapper" v-loading="loading">
      <div v-if="error" class="error-container">
        <el-result icon="error" title="加载失败" :subTitle="errorMsg">
          <template slot="extra">
            <el-button type="primary" size="small" @click="loadFileInfo">重新加载</el-button>
            <el-button size="small" @click="goBack">返回</el-button>
          </template>
        </el-result>
      </div>

      <div v-else-if="fileInfo" class="editor-content">
        <!-- 不支持编辑的文件类型 -->
        <div v-if="!isSupportEdit()" class="editor-unsupported">
          <el-result icon="warning" title="不支持编辑" subTitle="该文件类型暂不支持在线编辑">
            <template slot="extra">
              <el-button type="primary" size="small" @click="goBack">返回</el-button>
            </template>
          </el-result>
        </div>
        <!-- OnlyOffice 编辑器 -->
        <iframe v-else :key="editorKey" :src="getEditUrl()" frameborder="0" class="editor-iframe"
          @load="onEditorLoad" @error="onEditorError">
        </iframe>
      </div>

      <div v-else class="loading-container">
        <el-empty description="加载中..."></el-empty>
      </div>
    </div>
  </div>
</template>

<script>
import * as c from '@/utils/config'
import * as u from '@/utils/util'

export default {
  name: "editFile",
  components: {
    'user-pull-down': () => import('@/components/auth/userPullDown.vue'),
  },
  data() {
    return {
      fileId: '',
      fileInfo: null,
      loading: false,
      error: false,
      errorMsg: '',
      editorKey: 0,
      userName: '',
      userInfo: null,
      // 当前编辑文件的开始时间
      currentEditStartTime: null,
    }
  },
  created() {
    // 从路由参数获取文件ID
    this.fileId = this.$route.params.fileId || this.$route.query.fileId;
    
    if (!this.fileId) {
      this.error = true;
      this.errorMsg = '缺少文件ID参数';
      this.loading = false;
      return;
    }

    // 获取用户信息
    this.userInfo = u.getSession(c.sessionKey.USER_INFO);
    if (this.userInfo) {
      this.userName = this.userInfo.name || '用户';
    }

    // 加载文件信息
    this.loadFileInfo();
  },
  mounted() {
    // 监听页面离开事件，记录结束时间
    window.addEventListener('beforeunload', this.recordEditEndTime);
    // 监听页面可见性变化（切换标签页）
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  },
  beforeDestroy() {
    // 组件销毁前记录结束时间
    this.recordEditEndTime();
    // 移除事件监听
    window.removeEventListener('beforeunload', this.recordEditEndTime);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);

  },
  methods: {
     /**
     * 处理页面可见性变化（切换标签页）
     */
     handleVisibilityChange() {
      if (document.hidden) {
        // 页面被隐藏（切换到其他标签页），记录结束时间
        console.log('页面被隐藏，记录编辑结束时间');
        this.recordEditEndTime();
      } else {
        // 页面重新可见（切换回当前标签页），记录新的开始时间
        console.log('页面重新可见，记录新的编辑开始时间');
        if (this.fileInfo && this.fileInfo.fileId) {
          this.recordEditStartTime();
        }
      }
    },
    /**
     * 加载文件信息
     */
    loadFileInfo() {
      this.loading = true;
      this.error = false;

      this.$HttpService.post(`/file-info/detail`, { id: this.fileId }).then(res => {
        this.fileInfo = res || {};
        this.loading = false;

        // 记录编辑开始时间
        this.recordEditStartTime();
      }).catch(err => {
        this.error = true;
        this.errorMsg = '文件信息加载失败，请检查文件是否存在';
        this.loading = false;
      });
    },

    /**
     * 判断文件是否支持编辑
     */
    isSupportEdit() {
      if (!this.fileInfo || !this.fileInfo.fileType) {
        return false;
      }
      const fileType = this.fileInfo.fileType;
      return c.supportFile.edit.indexOf(fileType) !== -1;
    },

    /**
     * 获取编辑URL
     */
    getEditUrl() {
      if (!this.fileInfo || !this.fileInfo.fileId) {
        return "";
      }
      const token = u.getToken();
      const base = c.system.apiUrl;
      return `${base}/file-info/editFile/${this.fileInfo.fileId}?token=${token}`;
    },

    /**
     * 编辑器加载完成
     */
    onEditorLoad() {
      this.loading = false;
    },

    /**
     * 编辑器加载错误
     */
    onEditorError() {
      this.error = true;
      this.errorMsg = '编辑器加载失败';
      this.loading = false;
    },

    /**
     * 返回上一页
     */
    goBack() {
      // 记录结束时间
      this.recordEditEndTime();
      
      // 返回上一页或关闭窗口
      if (window.history.length > 1) {
        this.$router.go(-1);
      } else {
        window.close();
      }
    },

    /**
     * 记录编辑文件开始时间
     */
    recordEditStartTime() {
      if (!this.fileInfo || !this.fileInfo.fileId || !this.userInfo) {
        return;
      }

      // 格式化当前时间为 yyyy-MM-dd HH:mm:ss
      const now = new Date();
      this.currentEditStartTime = this.formatDateTime(now);

      // 调用接口记录开始时间
      this.$HttpService.post('/file-view/insert', {
        fileId: this.fileInfo.fileId,
        type: 2, // 2-编辑
        username: this.userInfo.username || this.userInfo.name,
        startTime: this.currentEditStartTime
      }).then(() => {
        console.log('记录编辑开始时间成功:', this.currentEditStartTime);
      }).catch(err => {
        console.error('记录编辑开始时间失败:', err);
      });
    },

    /**
     * 记录编辑文件结束时间
     */
    recordEditEndTime() {
      if (!this.fileInfo || !this.fileInfo.fileId || !this.currentEditStartTime || !this.userInfo) {
        return;
      }

      // 调用接口记录结束时间
      this.$HttpService.post('/file-view/updateEndTime', {
        fileId: this.fileInfo.fileId,
        type: 2, // 2-编辑
        username: this.userInfo.username || this.userInfo.name,
        startTime: this.currentEditStartTime
      }).then(() => {
        console.log('记录编辑结束时间成功');
        this.currentEditStartTime = null;
      }).catch(err => {
        console.error('记录编辑结束时间失败:', err);
      });
    },

    /**
     * 格式化日期时间为 yyyy-MM-dd HH:mm:ss
     */
    formatDateTime(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');

      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    },
  }
}
</script>

<style scoped lang="scss">
.edit-file-container {
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f5f5f5;

  .top_menu {
    width: 100%;
    height: 50px;
    display: flex;
    box-shadow: 0 0 4px rgba(0, 0, 0, 0.2);
    background: #fff;

    .logo {
      height: 40px;
      margin-top: 5px;
      vertical-align: top;
      margin-right: 8px;
    }

    .title {
      width: 300px;
      display: flex;
      align-items: center;
      padding-left: 20px;
      font-size: 16px;
      color: #363b4c;
      font-weight: 600;
    }

    .file-info {
      flex: 1;
      display: flex;
      align-items: center;
      padding-left: 20px;

      .file-name {
        font-size: 15px;
        color: #303133;
        font-weight: 500;
        max-width: 500px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }

    ul {
      width: auto;
      height: 100%;
      padding: 0 20px;
      color: #363b4c;
      list-style-type: none;
      text-align: right;
      margin: 0;

      li {
        line-height: 50px;
        display: inline-block;
        margin-left: 20px;
      }
    }
  }

  .editor-wrapper {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    .error-container,
    .loading-container {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #fff;
    }

    .editor-content {
      flex: 1;
      display: flex;
      flex-direction: column;
      background: #fff;

      .editor-iframe {
        width: 100%;
        height: 100%;
        border: none;
        background: #fff;
      }

      .editor-unsupported {
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }
  }
}
</style>

